<template>
  <div />
</template>

<script>

/**
 * Vue: Boost api call (boost-api-call)
 * -----------------------------------------------------------------------------
 * Component to make Boost API calls.
 * https://help.boostcommerce.net/category/668-filter-and-search-apis-app-customization
 *
 * @param {String} buildFilterTree - Leave blank if filters aren't needed or
 * endpoint is 'search/suggest', otherwise value is '&build_filter_tree=true'.
 * @param {String} choices - Formatted filter choices to use in API call.
 * @param {Number} collectionId - Collection id to use for 'filter' endpoint.
 * @param {String} endpoint - Determines which API to use. Options are 'filter'
 * (for collections), 'search' or 'search/suggest' (predictive search).
 * @param {Number} limit - Number of products to retrieve per page.
 * @param {String} shop - Permanent shop domain.
 * @param {String} query - Search query.
 *
 */
import productQuery from '~/graphql/queries/boost-data.gql'
import { decodeApiId } from '~/helpers/graphql'

export default {
  name: 'BoostApiCall',

  props: {
    buildFilterTree: {
      type: Boolean,
      default: true,
    },
    choices: {
      type: String,
      default: '',
    },
    collectionId: {
      type: Number,
      default: null,
    },
    endpoint: {
      type: String,
      default: '',
    },
    limit: {
      type: Number,
      default: null,
    },
    shop: {
      type: String,
      default: '',
    },
    query: {
      type: String,
      default: '',
    },
    page: {
      type: Number,
      default: null,
    },
  },

  emits: {
    'boost-updated': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      cnvsGlobal: true,
      hiddenProductIds: [],
    }
  },

  computed: {
    shopUrl() {
      return this.shop.replace('//', '').split('/')[0]
    },
  },

  watch: {

    /**
     * Watch choices update to get product results.
     */
    choices(choices) {
      this.getProductResults(choices)
    },

    /**
     * Watch query update to get product results.
     */
    query() {
      this.getProductResults(this.choices)
    },

    /**
     * Watch page update to get product results.
     */
    page() {
      this.getProductResults(this.choices)
    },
  },

  methods: {

    /**
     * Format filter tree param for API call.
     * @returns {String}
     */
    getFilters() {
      if (this.buildFilterTree === true) {
        return '&build_filter_tree=true'
      }

      return ''
    },

    /**
     * Check collection ID before making API call.
     * @returns {String}
     */
    getCollectionScope() {
      let collectionScope = `&collection_scope=${this.collectionId}`

      if (this.collectionId === null) {
        collectionScope = ''
      }

      return collectionScope
    },

    /**
     * Check products per page limit before making API call.
     * @returns {String}
     */
    getProductsPerPage() {
      let limit = `&limit=${this.limit}`

      if (isNaN(this.limit)) {
        limit = ''
      }

      return limit
    },

    /**
     * Get and format search query for search/suggest endpoint.
     * @returns {String}
     */
    getQuery() {
      let query = ''

      if (this.endpoint === 'search' || this.endpoint === 'search/suggest') {
        query = `&q=${this.query}&fuzzy=2`
      }

      return query
    },

    /**
     * Convert product price for Shopify markets. Boost does not handle markets
     * fully, in addition an extra network call was required to enable market
     * pricing with Boost data.
     * @param {object} data - Product data.
     */
    getConvertedProductPrices(data) {
      if (!data) {
        return false
      }

      return new Promise(async(resolve, reject) => {
        const query = data.map((object) => `id:${object.id}`).join(' OR ')

        try {
          const response = await cnvs.Query({
            query: productQuery,
            variables: {
              country: this.$variable('store.country'),
              query,
              language: this.$variable('store.language'),
            },
          })

          if (!response?.products?.edges?.length) {
            resolve(false)
            return
          }
          resolve(response?.products?.edges ?? false)
        } catch (error) {
          reject(error)
        }
      })
    },

    /**
     * Handle API call to Boost.
     * @param {String} choices - Search parameters to add to request url.
     */
    async getProductResults(choices) {
      try {
        const buildFilterTree = this.getFilters()
        const collectionScope = this.getCollectionScope()
        const limit = this.getProductsPerPage()
        const query = this.getQuery()
        let formattedChoices = choices

        if (this.endpoint === 'search/suggest' && this.query === '') {
          return
        }

        if (!choices) {
          formattedChoices = ''
        }

        let url = `https://services.mybcapps.com/bc-sf-filter/${this.endpoint}?shop=${this.shopUrl}`

        url += buildFilterTree
        url += collectionScope
        url += limit
        url += query
        url += formattedChoices
        url += `&lang=${cnvs.store.language}`

        if (this.page) {
          url += `&page=${this.page}`
        }

        const response = await fetch(url)
        const data = await response.json()
        const localisedPrices = await this.getConvertedProductPrices(data.products)

        const filteredProducts = data.products.map((product) => {
          if(this.query === '' && product.tags.includes('[hide_from_collections]')){

            if(!this.hiddenProductIds.includes(product.id)){
              this.hiddenProductIds.push(product.id)
            }

            return null
          }
          return product
        }).filter(Boolean)

        filteredProducts.forEach((product) => {
          const localProduct = localisedPrices?.find((price) => {
            return decodeApiId(price.node.id) === product.id
          })

          if (!localProduct) {
            return
          }

          product.price_min = Number(localProduct.node.priceRange.minVariantPrice.amount)
          product.price_max = Number(localProduct.node.priceRange.maxVariantPrice.amount)
        })

        this.$emit('boost-updated', {
          ...data,
          products: filteredProducts,
          total_product: data.total_product - this.hiddenProductIds.length,
        })

      } catch (error) {
        throw new Error(error)
      }
    },
  },
}
</script>
